import {
  PublicRequestError,
  playerApi,
  type PlayerParty,
  type PlayerPartyInvite,
} from '../api/public.ts'
import {
  isPartyConcurrencyError,
  validatePlayerPartyVersion,
  type PlayerPartyVersion,
} from './playerPartyVersion.ts'
import {
  compareDecimalIDs,
  partyMembershipKey,
  playerPartyInviteAcceptanceErrorMessage,
} from './playerSocialPresentation.ts'
import type { PlayerSocialDockSession } from './playerSocialDockRuntime.ts'
import { computed, ref } from 'vue'

type PlayerPartyDockDependencies = {
  session: PlayerSocialDockSession
  refreshChat: (forceReplace?: boolean) => Promise<void>
  invalidatePartyChat: (notice?: string | null) => void
  onMembershipChanged: (
    previousMembership: string,
    nextMembership: string,
  ) => void
  onIncomingInvitesCommitted: (
    invites: readonly PlayerPartyInvite[],
    observedAt?: number,
  ) => void
}

type CommittedPartyState = {
  party: PlayerParty | null
  version: PlayerPartyVersion | null
}

type PartyActionOptions = {
  requireHealthy?: boolean
  onError?: (error: unknown) => void | Promise<void>
  errorMessage?: (error: unknown) => string
}

const partyRetryDelaysMs = [2_000, 5_000, 15_000, 30_000] as const
const invalidPartyVersionMessage =
  'Сервер вернул некорректную версию состава. Действия временно заблокированы.'

export function usePlayerSocialDockParty(
  dependencies: PlayerPartyDockDependencies,
) {
  const { session } = dependencies
  const { options } = session
  const committedPartyState = ref<CommittedPartyState>({
    party: null,
    version: null,
  })
  const party = computed(() => committedPartyState.value.party)
  const partyVersion = computed(() => committedPartyState.value.version)
  const partyETag = computed(() => partyVersion.value?.etag ?? null)
  const incomingPartyInvites = ref<PlayerPartyInvite[]>([])
  const partyLoading = ref(false)
  const partyStateHealthy = ref(false)
  const partyActionPending = ref(false)
  const partyError = ref<string | null>(null)
  const partyInviteCooldownRetry = ref<{
    steamID: string
    seconds: number
  } | null>(null)

  const isPartyLeader = computed(() => {
    const current = party.value
    return Boolean(current && current.leader_steam_id === options.steamID())
  })

  let partyRequestID = 0
  let partyActionRequestID = 0
  let partyRetryTimer: number | undefined
  let partyRetryAttempt = 0
  let incomingInviteNotificationObservedAt: number | undefined

  function reset(): void {
    partyRequestID += 1
    partyActionRequestID += 1
    clearRetryTimer()
    partyRetryAttempt = 0
    incomingInviteNotificationObservedAt = undefined
    committedPartyState.value = { party: null, version: null }
    incomingPartyInvites.value = []
    partyLoading.value = false
    partyStateHealthy.value = false
    partyActionPending.value = false
    partyError.value = null
    partyInviteCooldownRetry.value = null
  }

  function stop(): void {
    clearRetryTimer()
  }

  async function refreshParty(
    preserveValidatedState = false,
    notificationObservedAt?: number,
  ): Promise<void> {
    if (!options.authenticated()) return
    incomingInviteNotificationObservedAt = earliestObservedAt(
      incomingInviteNotificationObservedAt,
      notificationObservedAt,
    )
    clearRetryTimer()
    const generation = session.generation()
    const actorSteamID = options.steamID()
    if (!actorSteamID) return
    const requestID = ++partyRequestID
    partyLoading.value = true
    if (!preserveValidatedState) partyStateHealthy.value = false
    try {
      const snapshot = await playerApi.partyState()
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        partyRequestID,
      )) {
        return
      }
      const valid = commitPartySnapshot(
        snapshot.state.party,
        snapshot.etag,
        snapshot.state.party_etag,
        snapshot.state.incoming_invites,
      )
      if (!valid) {
        scheduleRetry()
        return
      }
      partyRetryAttempt = 0
      partyError.value = null
    } catch (error) {
      if (!session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        partyRequestID,
      )) {
        return
      }
      session.handleRequestError(error, partyError)
      if (!preserveValidatedState || !partyStateHealthy.value) {
        partyStateHealthy.value = false
      }
      scheduleRetry()
    } finally {
      if (session.isCurrentActorRequest(
        generation,
        actorSteamID,
        requestID,
        partyRequestID,
      )) {
        partyLoading.value = false
      }
    }
  }

  async function endCurrentParty(action: 'leave' | 'disband'): Promise<boolean> {
    const current = party.value
    const version = partyVersion.value
    if (!current || !version) return false
    const leader = current.leader_steam_id === options.steamID()
    if (action === 'disband' && !leader) return false
    if (action === 'leave' && leader) return false
    return runPartyAction(async isCurrent => {
      if (action === 'disband') {
        await playerApi.disbandParty(version.etag)
      } else {
        await playerApi.leaveParty(version.etag)
      }
      if (!isCurrent()) return
      invalidatePartyReads()
      commitPartyState(null, null, [])
      partyStateHealthy.value = true
      partyError.value = null
      dependencies.invalidatePartyChat()
      await dependencies.refreshChat(true)
      if (!isCurrent()) return
      convergePartyState()
    })
  }

  function leaveParty(): Promise<boolean> {
    return endCurrentParty('leave')
  }

  function disbandParty(): Promise<boolean> {
    return endCurrentParty('disband')
  }

  async function inviteFriendToParty(steamID: string): Promise<boolean> {
    partyInviteCooldownRetry.value = null
    return runPartyAction(async isCurrent => {
      const previousParty = party.value
      const result = await playerApi.invitePartyMember(
        steamID,
        partyVersion.value?.etag ?? null,
      )
      if (!isCurrent()) return
      const invite = result.data.invite
      if (invite.party_id !== invite.party.id) {
        partyError.value = invalidPartyVersionMessage
        await refreshParty()
        if (!isCurrent()) return
        await dependencies.refreshChat()
        convergePartyState()
        return
      }
      const valid = adoptPartyMutation(
        invite.party,
        result.etag,
        result.data.party_etag ?? invite.party_etag,
        previousParty ? undefined : [],
      )
      if (!valid) {
        await refreshParty()
      }
      if (!isCurrent()) return
      await dependencies.refreshChat()
      convergePartyState()
    }, {
      onError: error => {
        if (
          error instanceof PublicRequestError &&
          error.code === 'party_invite_cooldown'
        ) {
          partyInviteCooldownRetry.value = {
            steamID,
            seconds: error.retryAfterSeconds ?? 10,
          }
        }
      },
    })
  }

  async function acceptPartyInvite(invite: PlayerPartyInvite): Promise<boolean> {
    if (invite.party.id !== invite.party_id) {
      partyError.value = invalidPartyVersionMessage
      void refreshParty()
      return false
    }
    const invitationVersion = validatePlayerPartyVersion(
      {
        ...invite.party,
        id: invite.party_id,
        revision: invite.created_at_revision,
      },
      { jsonETag: invite.party_etag },
    )
    if (!invitationVersion.valid || !invitationVersion.version) {
      partyError.value = invalidPartyVersionMessage
      void refreshParty()
      return false
    }
    const invitationETag = invitationVersion.version.etag

    return runPartyAction(async isCurrent => {
      let result: Awaited<ReturnType<typeof playerApi.acceptPartyInvite>>
      try {
        result = await playerApi.acceptPartyInvite(
          invite.id,
          invitationETag,
        )
      } catch (error) {
        if (
          !(error instanceof PublicRequestError) ||
          error.code !== 'party_invite_not_found'
        ) {
          throw error
        }
        await refreshParty(true)
        if (!isCurrent()) return
        await dependencies.refreshChat(true)
        if (!isCurrent()) return
        if (party.value?.id === invite.party_id) {
          return
        }
        throw error
      }
      if (!isCurrent()) return
      const valid = adoptPartyMutation(
        result.data.party,
        result.etag,
        result.data.party_etag,
        [],
      )
      if (!valid) {
        await refreshParty()
      }
      if (!isCurrent()) return
      await dependencies.refreshChat(true)
      convergePartyState()
    }, {
      onError: async error => {
        if (
          error instanceof PublicRequestError &&
          (
            error.code === 'actor_already_in_party' ||
            error.code === 'already_in_party'
          )
        ) {
          await refreshParty(true)
          await dependencies.refreshChat(true)
        }
      },
      errorMessage: playerPartyInviteAcceptanceErrorMessage,
    })
  }

  async function declinePartyInvite(inviteID: string): Promise<boolean> {
    return runPartyAction(async isCurrent => {
      await playerApi.declinePartyInvite(inviteID)
      if (!isCurrent()) return
      incomingPartyInvites.value = incomingPartyInvites.value.filter(
        invite => invite.id !== inviteID,
      )
      dependencies.onIncomingInvitesCommitted(incomingPartyInvites.value)
      await refreshParty(true)
    }, { requireHealthy: false })
  }

  async function declineAllPartyInvites(): Promise<boolean> {
    return runPartyAction(async isCurrent => {
      await playerApi.declineAllPartyInvites()
      if (!isCurrent()) return
      incomingPartyInvites.value = []
      dependencies.onIncomingInvitesCommitted([])
      await refreshParty(true)
    }, { requireHealthy: false })
  }

  async function kickPartyMember(steamID: string): Promise<boolean> {
    const version = partyVersion.value
    if (!version) return false
    return runPartyAction(async isCurrent => {
      const result = await playerApi.kickPartyMember(steamID, version.etag)
      if (!isCurrent()) return
      const valid = adoptPartyMutation(
        result.data.party,
        result.etag,
        result.data.party_etag,
      )
      if (!valid) {
        await refreshParty()
      }
      if (!isCurrent()) return
      await dependencies.refreshChat()
      convergePartyState()
    })
  }

  async function transferPartyLeadership(steamID: string): Promise<boolean> {
    const version = partyVersion.value
    if (!version) return false
    return runPartyAction(async isCurrent => {
      const result = await playerApi.transferPartyLeadership(
        steamID,
        version.etag,
      )
      if (!isCurrent()) return
      const valid = adoptPartyMutation(
        result.data.party,
        result.etag,
        result.data.party_etag,
      )
      if (!valid) {
        await refreshParty()
      }
      if (!isCurrent()) return
      convergePartyState()
    })
  }

  async function runPartyAction(
    action: (isCurrent: () => boolean) => Promise<void>,
    actionOptions: PartyActionOptions = {},
  ): Promise<boolean> {
    const requireHealthy = actionOptions.requireHealthy !== false
    if (
      partyActionPending.value ||
      (requireHealthy && !partyStateHealthy.value)
    ) {
      if (requireHealthy && !partyStateHealthy.value) {
        partyError.value = 'Состав группы ещё не синхронизирован.'
      }
      return false
    }
    const generation = session.generation()
    const actorSteamID = options.steamID()
    if (!actorSteamID) return false
    const requestID = ++partyActionRequestID
    const isCurrent = () => session.isCurrentActorRequest(
      generation,
      actorSteamID,
      requestID,
      partyActionRequestID,
    )
    partyActionPending.value = true
    partyError.value = null
    try {
      await action(isCurrent)
      if (!isCurrent()) return false
      options.onPartyChanged()
      return true
    } catch (error) {
      if (!isCurrent()) return false
      await actionOptions.onError?.(error)
      if (!isCurrent()) return false
      if (isPartyConcurrencyError(error)) {
        await refreshParty()
        await dependencies.refreshChat(true)
      }
      if (!isCurrent()) return false
      session.handleRequestError(error, partyError, actionOptions.errorMessage)
      return false
    } finally {
      if (isCurrent()) {
        partyActionPending.value = false
      }
    }
  }

  function adoptPartyMutation(
    nextParty: PlayerParty | null,
    headerETag: string | null,
    jsonETag: string | null | undefined,
    nextInvites?: PlayerPartyInvite[],
  ): boolean {
    invalidatePartyReads()
    return commitPartySnapshot(
      nextParty,
      headerETag,
      jsonETag,
      nextInvites,
    )
  }

  function commitPartySnapshot(
    nextParty: PlayerParty | null,
    headerETag: string | null,
    jsonETag: string | null | undefined,
    nextInvites?: PlayerPartyInvite[],
  ): boolean {
    const validation = validatePlayerPartyVersion(nextParty, {
      headerETag,
      jsonETag,
    })
    if (!validation.valid) {
      if (nextInvites) commitIncomingPartyInvites(nextInvites)
      partyStateHealthy.value = false
      partyError.value = invalidPartyVersionMessage
      return false
    }
    const currentVersion = partyVersion.value
    if (
      validation.version &&
      currentVersion &&
      validation.version.partyID === currentVersion.partyID &&
      compareDecimalIDs(
        validation.version.revision,
        currentVersion.revision,
      ) < 0
    ) {
      partyStateHealthy.value = true
      return true
    }
    commitPartyState(nextParty, validation.version, nextInvites)
    partyStateHealthy.value = true
    return true
  }

  function commitPartyState(
    nextParty: PlayerParty | null,
    nextVersion: PlayerPartyVersion | null,
    nextInvites?: PlayerPartyInvite[],
  ): void {
    const actorSteamID = options.steamID()
    const previousMembership = partyMembershipKey(party.value, actorSteamID)
    const nextMembership = partyMembershipKey(nextParty, actorSteamID)
    committedPartyState.value = {
      party: nextParty,
      version: nextVersion,
    }
    if (nextInvites) commitIncomingPartyInvites(nextInvites)
    if (previousMembership !== nextMembership) {
      dependencies.onMembershipChanged(previousMembership, nextMembership)
    }
  }

  function commitIncomingPartyInvites(
    nextInvites: PlayerPartyInvite[],
  ): void {
    const validInvites = nextInvites.filter(invite => {
      if (invite.party_id !== invite.party.id) return false
      const invitationVersion = validatePlayerPartyVersion(
        {
          ...invite.party,
          id: invite.party_id,
          revision: invite.created_at_revision,
        },
        { jsonETag: invite.party_etag },
      )
      return invitationVersion.valid && Boolean(invitationVersion.version)
    })
    incomingPartyInvites.value = validInvites
    const observedAt = incomingInviteNotificationObservedAt
    incomingInviteNotificationObservedAt = undefined
    dependencies.onIncomingInvitesCommitted(validInvites, observedAt)
  }

  function invalidatePartyReads(): void {
    partyRequestID += 1
    partyLoading.value = false
  }

  function convergePartyState(): void {
    void refreshParty(true)
  }

  function scheduleRetry(): void {
    if (
      typeof window === 'undefined' ||
      !options.authenticated() ||
      (typeof document !== 'undefined' && document.hidden)
    ) {
      return
    }
    clearRetryTimer()
    const delay = partyRetryDelaysMs[
      Math.min(partyRetryAttempt, partyRetryDelaysMs.length - 1)
    ]
    partyRetryAttempt += 1
    partyRetryTimer = window.setTimeout(() => {
      partyRetryTimer = undefined
      if (
        !options.authenticated() ||
        partyLoading.value ||
        partyActionPending.value
      ) {
        scheduleRetry()
        return
      }
      void refreshParty().then(() => {
        if (partyStateHealthy.value) return dependencies.refreshChat()
      })
    }, delay)
  }

  function clearRetryTimer(): void {
    if (partyRetryTimer === undefined || typeof window === 'undefined') return
    window.clearTimeout(partyRetryTimer)
    partyRetryTimer = undefined
  }

  function removeInvitesByInviter(steamID: string): void {
    incomingPartyInvites.value = incomingPartyInvites.value.filter(
      invite => invite.invited_by_steam_id !== steamID,
    )
  }

  function clearError(): void {
    partyError.value = null
  }

  function earliestObservedAt(
    current: number | undefined,
    candidate: number | undefined,
  ): number | undefined {
    if (!Number.isFinite(candidate)) return current
    return current === undefined
      ? candidate
      : Math.min(current, candidate as number)
  }

  return {
    party,
    partyVersion,
    partyETag,
    incomingPartyInvites,
    partyLoading,
    partyStateHealthy,
    partyActionPending,
    partyError,
    partyInviteCooldownRetry,
    isPartyLeader,
    reset,
    stop,
    refreshParty,
    leaveParty,
    disbandParty,
    inviteFriendToParty,
    acceptPartyInvite,
    declinePartyInvite,
    declineAllPartyInvites,
    kickPartyMember,
    transferPartyLeadership,
    removeInvitesByInviter,
    clearRetryTimer,
    clearError,
  }
}
